/**
 * Closing the sheet while its purchase runs after Buy (Erik, 5 Nov 2026:
 * "4 yes, correct"). While the attempt view offers its stop (after Buy,
 * before the Pay claim: the server decides), the sheet's close asks "Stop
 * this purchase?". Yes stops it by that same stop (the attempt's cancel,
 * the spine's `requestCancel` for the fan who owns it); No keeps it going
 * and the sheet closes as it does without the question. Once Pay is
 * claimed the view offers no stop, and the close asks nothing new.
 *
 * Only the server's stopped ending is a stop: a purchase that Pay reached
 * first (claimed, bought or uncertain) is too late to stop, says so, and the
 * sheet stays on it as it goes on. The question is asked, and its stop
 * sent, only while the view offers the stop at that moment: once it no
 * longer does, the question gives way. Web and app sheets both ask with
 * these words.
 */
import type { PurchaseJourney, PurchaseJourneyState } from "./journey";
import { attemptOpen } from "./transport";

export const CLOSE_STOP_WORDS = {
  title: "Stop this purchase?",
  message: "Nothing has been paid yet.",
  stop: "Yes, stop it",
  keep: "No, keep going",
  tooLate: {
    title: "Too late to stop",
    message: "Payment has started, so this purchase can't be stopped now. You'll see how it goes here.",
    done: "OK",
  },
} as const;

/** The same words for a browser's own dialogs (`window.confirm`, its OK
 *  as Yes, and `window.alert`), where a sheet draws none of its own. */
export const CLOSE_STOP_DIALOG = {
  ask: `${CLOSE_STOP_WORDS.title} ${CLOSE_STOP_WORDS.message}\n\nOK: ${CLOSE_STOP_WORDS.stop}.\nCancel: ${CLOSE_STOP_WORDS.keep}.`,
  tooLate: `${CLOSE_STOP_WORDS.tooLate.title}. ${CLOSE_STOP_WORDS.tooLate.message}`,
} as const;

/** What came of the fan's Yes: stopped (the server's stopped ending); too
 *  late (Pay reached it first: claimed, bought or uncertain); or not
 *  stopped, for another reason the checkout says itself. */
export type CloseStopResult = "stopped" | "too_late" | "not_stopped";

/** What the sheet's close says of its purchase now: asks whether to stop
 *  it (the view offers its stop); says it is too late to stop it (Pay
 *  reached it); or nothing new (null: no purchase, or one ended without
 *  Pay). */
export type CloseStopMoment = "ask" | "too_late" | null;

/** The purchase as the sheet's close may stop it, told to the sheet at each
 *  change of its moment: the moment, and the stop, which sends nothing
 *  unless the view offers it when it is pressed. */
export type CloseStop = { moment: Exclude<CloseStopMoment, null>; stop: () => Promise<CloseStopResult> };

const offersStop = (state: Pick<PurchaseJourneyState, "attempt">): boolean =>
  attemptOpen(state.attempt) && !!state.attempt?.view?.actions.some((action) => action.kind === "stop");

export function closeStopMoment(state: Pick<PurchaseJourneyState, "attempt">): CloseStopMoment {
  if (offersStop(state)) return "ask";
  const view = state.attempt?.view;
  if (!view) return null;
  if (view.payClaimed) return "too_late";
  const ended = view.lifecycle === "ended" && view.screen.kind === "outcome" ? view.screen.result : null;
  return ended === "bought" || ended === "uncertain" ? "too_late" : null;
}

/** The sheet's close asks to stop the purchase: its attempt runs and its
 *  view offers its stop. */
export const closeAsksStop = (state: Pick<PurchaseJourneyState, "attempt">): boolean =>
  closeStopMoment(state) === "ask";

/** The fan said Yes: the attempt's stop, sent only while the view offers it
 *  now, then what came of it, by the server's answer alone. */
export async function stopOnClose(journey: Pick<PurchaseJourney, "cancel" | "snapshot">): Promise<CloseStopResult> {
  if (closeAsksStop(journey.snapshot())) await journey.cancel();
  const state = journey.snapshot();
  const view = state.attempt?.view;
  if (view?.lifecycle === "ended" && view.screen.kind === "outcome" && view.screen.result === "stopped") return "stopped";
  return closeStopMoment(state) === "too_late" ? "too_late" : "not_stopped";
}
